import React, { useState } from 'react'
import { capitalize } from 'lodash'
import { useIntl } from 'react-intl'
import { Box } from '@mui/system'
import {
    Collapse,
    IconButton,
    useTheme,
    LinearProgress,
    CircularProgress,
} from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import LockIcon from '@mui/icons-material/Lock'
import TV from '../../../../assets/icons/devices/Tv.svg'
import Document from '../../../../assets/icons/Documents/Document.svg'
import { sectionApiSlice } from '../../../../features/section/sectionSlice'
import {
    CourseState,
    SectionDetailsState,
    SectionState,
    SubjectState,
    UserTopicState,
} from '../../../../features/types'
import Section from './Section'
import TopicContent from './TopicContent'

interface Props {
    topic: UserTopicState
    selectedSection: SectionState
    isFetchingSectionDetails: boolean
    isFetchingSections: boolean
    sectionDetails: SectionDetailsState
    selectedTopic: UserTopicState
    selectedTopicSections: SectionState[]
    currentSectionIndex: number
    userSubjectEnrollId: number
    handleSelect: (topic: UserTopicState, section: SectionState) => void
    onPreviousSection: () => void
    onNextSection: () => void
    subject: SubjectState
    course: CourseState
}
const Topic: React.FC<Props> = ({
    topic,
    selectedSection,
    handleSelect,
    isFetchingSectionDetails,
    isFetchingSections,
    sectionDetails,
    selectedTopic,
    selectedTopicSections,
    currentSectionIndex,
    userSubjectEnrollId,
    onPreviousSection,
    onNextSection,
    subject,
    course,
}) => {
    const { formatMessage, locale } = useIntl()
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)

    const { data: sections = [], isFetching: isFetchingTopicSections } =
        sectionApiSlice.useGetStudentSectionsByTopicEnrollQuery(
            topic.userTopicEnrollId,
            {
                skip: !expanded,
            },
        )

    const styles = {
        wrapper: {
            border: `1px solid ${theme.palette.grey[100]}`,
            borderRadius: '8px',
            mb: 2,
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: theme.palette.grey[100],
            cursor: 'pointer',
            p: 1,
            pr: 2,
        },
        chevron: {
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
    }

    const handleExpand = () => {
        if (topic.userTopicEnrollId) {
            setExpanded(!expanded)
        }
    }

    return (
        <Box sx={styles.wrapper}>
            <Box
                sx={styles.container}
                onClick={handleExpand}
            >
                <Box
                    sx={styles.flexCenter}
                    style={{ width: '80%' }}
                >
                    <IconButton disableRipple={!topic.userTopicEnrollId}>
                        {topic.userTopicEnrollId ? (
                            <ChevronRight sx={styles.chevron} />
                        ) : (
                            <LockIcon />
                        )}
                    </IconButton>

                    <Box
                        ml={1}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            // width: '100%',
                            flexGrow: 1,
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box>
                                {topic[`topicTitle${capitalize(locale)}`]}
                            </Box>

                            <Box>{topic.percentage ?? '0'}%</Box>
                        </Box>

                        <Box>
                            <LinearProgress
                                variant="determinate"
                                color={'primary'}
                                value={topic.percentage}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={styles.flexCenter}>
                    <Box sx={styles.flexCenter}>
                        <img
                            alt={'tv'}
                            src={TV}
                        />
                        {topic.countVideo}
                    </Box>

                    <Box sx={{ ...styles.flexCenter, mr: 1, ml: 1 }}>
                        <img
                            alt={'document'}
                            src={Document}
                        />
                        {topic.countTest}
                    </Box>
                </Box>
            </Box>

            <Collapse in={expanded}>
                {isFetchingTopicSections && <CircularProgress sx={{ m: 1 }} />}

                <Box>
                    {!sections.length ? (
                        <Box
                            sx={{
                                padding: 2,
                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                            }}
                        >
                            {formatMessage({ id: 'noData' })}
                        </Box>
                    ) : (
                        sections.map((section, index) => {
                            return (
                                <Box key={section.id}>
                                    <Section
                                        section={section}
                                        isLast={index === sections.length - 1}
                                        handleSelect={() =>
                                            handleSelect(topic, section)
                                        }
                                        isFetchingSectionDetails={
                                            isFetchingSectionDetails
                                        }
                                        isFetchingSections={isFetchingSections}
                                        sectionDetails={sectionDetails}
                                        selectedSection={selectedSection}
                                        selectedTopic={selectedTopic}
                                        selectedTopicSections={
                                            selectedTopicSections
                                        }
                                        currentSectionIndex={
                                            currentSectionIndex
                                        }
                                        userSubjectEnrollId={
                                            userSubjectEnrollId
                                        }
                                        onPreviousSection={onPreviousSection}
                                        onNextSection={onNextSection}
                                    />

                                    <Collapse
                                        sx={{
                                            '@media screen and (min-width: 500px)':
                                                {
                                                    display: 'none',
                                                },
                                        }}
                                        in={selectedSection?.id === section?.id}
                                    >
                                        <Box
                                            sx={{
                                                padding: 2,
                                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                            }}
                                        >
                                            <TopicContent
                                                course={course}
                                                subject={subject}
                                                isFetchingSectionDetails={
                                                    isFetchingSectionDetails
                                                }
                                                isFetchingSections={
                                                    isFetchingSections
                                                }
                                                sectionDetails={sectionDetails}
                                                selectedSection={
                                                    selectedSection as SectionState
                                                }
                                                selectedTopic={selectedTopic}
                                                selectedTopicSections={
                                                    selectedTopicSections
                                                }
                                                currentSectionIndex={
                                                    currentSectionIndex
                                                }
                                                userSubjectEnrollId={
                                                    userSubjectEnrollId
                                                }
                                                onPreviousSection={
                                                    onPreviousSection
                                                }
                                                onNextSection={onNextSection}
                                            />
                                        </Box>
                                    </Collapse>
                                </Box>
                            )
                        })
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}

export default Topic
